import React from 'react';
import { Controller, Control } from 'react-hook-form';
import { FormLabel, TextField, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Moment } from 'moment';

import { getFormattedDateRangeWithMultiDaySupport } from 'utils/getFormattedDateRangeWithMultiDaySupport';
import { FormValues } from './index';

interface DateRangeFieldsProps {
  control: Control<FormValues>;
  startDate: Moment | null;
  endDate: Moment | null;
}

export const DateRangeFields = ({ control, startDate, endDate }: DateRangeFieldsProps) => {
  const { datePart, utcPart } = getFormattedDateRangeWithMultiDaySupport({
    startingAt: startDate?.toISOString(),
    timeZone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
    endingAt: endDate?.toISOString(),
    withTime: true,
  });

  return (
    <div>
      <FormLabel component="legend" sx={{ mb: 2.5 }}>
        Date Range (optional)
      </FormLabel>
      <div style={{ display: 'flex', gap: '10px' }}>
        <Controller
          name="startingAt"
          control={control}
          rules={{
            validate: (value) => {
              if (endDate && !value) {
                return 'Start date is required when end date is provided';
              }
              if (value && endDate && value.isAfter(endDate)) {
                return 'Start date must be before end date';
              }
              return true;
            },
          }}
          render={({ field: { value, onChange }, fieldState }) => (
            <DateTimePicker
              label="Start date"
              value={value}
              onChange={onChange}
              inputFormat="DD/MM/YYYY hh:mm A"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          name="endingAt"
          control={control}
          rules={{
            validate: (value) => {
              if (startDate && !value) {
                return 'End date is required when start date is provided';
              }
              if (value && startDate && value.isBefore(startDate)) {
                return 'End date must be after start date';
              }
              return true;
            },
          }}
          render={({ field: { value, onChange }, fieldState }) => (
            <DateTimePicker
              label="End date"
              value={value}
              onChange={onChange}
              inputFormat="DD/MM/YYYY hh:mm A"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
      </div>
      {startDate && endDate && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Rule will apply from {datePart} {utcPart}.
        </Typography>
      )}
    </div>
  );
};
